import { getOrCreateFormattedTimezone, getTimeOfDayIcon } from '@99designs/design-services-common';
import { getCurrentCulture } from '@99designs/i18n';
import type { VideoCallConfirmation } from '../types/chat';

/**
 * Derive a full VideoCallConfirmation display payload from an ISO UTC instant
 * and an optional IANA timezone string.
 *
 * Used by `renderMessageView` when rehydrating chat history. Falls back to the
 * browser's current IANA timezone when no timezone is stored (legacy bare-ISO
 * messages).
 */
export function reconstructVideoConfirmation(
    appointmentTime: string,
    timezone?: string
): VideoCallConfirmation | null {
    const instant = new Date(appointmentTime);
    if (Number.isNaN(instant.getTime())) {
        return null;
    }

    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneDisplay = getOrCreateFormattedTimezone(tz, instant).displayName;
    const locale = getCurrentCulture();

    return {
        date: instant.toLocaleDateString(locale, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: tz,
        }),
        time: `${instant.toLocaleTimeString(locale, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: tz,
        })} ${getTimeOfDayIcon(tz, instant)}`,
        timezone: timezoneDisplay,
        scheduledTimeUTC: appointmentTime,
    };
}
